import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  App,
  Button,
  Card,
  Descriptions,
  Drawer,
  Input,
  List,
  Modal,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from "antd";
import LeadDetailsDrawer from "../../../Components/Agent/AISales/LeadDetailsDrawer";
import CustomerVacationChatAssistantText from "../../../Components/Client/CustomerVacationChat/CustomerVacationChatAssistantText";
import { getAiSalesLeadDetail } from "../../../services/aiSalesService";
import { getUserDetails, isAdminUser } from "../../../services/authService";
import { useAgentEntitlements } from "../../../features/agentEntitlements/useAgentEntitlements";
import { navigateAgentPackageUpgrade } from "../../../utils/agentUpgradeNavigation";
import {
  assignCustomerChatSessionLead,
  getCustomerChatPublicLink,
  getCustomerChatSessionDetail,
  listCustomerChatSessions,
  markCustomerChatSessionRead,
} from "../../../services/customerChatAgentService";
import { resolvePublicBaseUrl } from "../../../utils/resolvePublicBaseUrl";
import "./CustomerChatInboxPage.css";

function fmtTs(v) {
  if (v == null || v === "") return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("DD/MM/YYYY HH:mm") : String(v);
}

export default function CustomerChatInboxPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const {
    status: entitlementsStatus,
    canUseAiSales,
    loading: entitlementsLoading,
    refresh: refreshEntitlements,
  } = useAgentEntitlements();
  const salesAllowed = canUseAiSales;
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignAgentId, setAssignAgentId] = useState("");
  const [assignBusy, setAssignBusy] = useState(false);
  const [leadDrawerOpen, setLeadDrawerOpen] = useState(false);
  const [leadDetail, setLeadDetail] = useState(null);
  const [leadDetailLoading, setLeadDetailLoading] = useState(false);

  const isAdmin = isAdminUser();
  const currentUser = getUserDetails();

  const copyPublicChatLink = async () => {
    if (!salesAllowed) return;
    try {
      const adminAgentId =
        currentUser?.id != null && String(currentUser.id).trim() !== ""
          ? String(currentUser.id).trim()
          : null;
      if (isAdmin && !adminAgentId) {
        if (process.env.NODE_ENV === "development") {
          console.log("[AI_CHAT_DEBUG_FE]", {
            action: "public_link_validation_failed",
            isAdmin,
            currentUser,
            reason: "missing_admin_agent_id",
          });
        }
        message.error("לא נמצא מזהה סוכן עבור האדמין. נסו התחברות מחדש.");
        return;
      }
      if (process.env.NODE_ENV === "development") {
        console.log("[AI_CHAT_DEBUG_FE]", {
          action: "public_link_request_start",
          isAdmin,
          userId: currentUser?.id ?? null,
          userEmail: currentUser?.email ?? null,
          agentIdSent: isAdmin ? adminAgentId : null,
        });
      }
      const res = await getCustomerChatPublicLink({
        agentId: isAdmin ? adminAgentId : undefined,
      });
      if (process.env.NODE_ENV === "development") {
        console.log("[AI_CHAT_DEBUG_FE]", {
          action: "public_link_response",
          response: res,
        });
      }
      if (res.status !== "ok" || !res.payload?.ref) {
        message.error(res.message || "לא ניתן להפיק קישור");
        return;
      }
      const ref = encodeURIComponent(String(res.payload.ref));
      const url = `${resolvePublicBaseUrl({ skipTrace: true })}/customer-chat/start?ref=${ref}`;
      await navigator.clipboard.writeText(url);
      message.success(
        isAdmin && res.payload?.used_default_agent
          ? "הקישור הועתק (סוכן ברירת מחדל — בדיקת אדמין)"
          : "הקישור הועתק — אפשר לשלוח בוואטסאפ"
      );
    } catch (e) {
      message.error(e?.message || "שגיאה בהעתקה");
    }
  };

  const loadList = useCallback(async () => {
    if (!salesAllowed) return;
    setLoading(true);
    try {
      const res = await listCustomerChatSessions({ limit: 150 });
      if (res.status === "ok") {
        setSessions(res.payload?.sessions || []);
      } else {
        message.error(res.message || "שגיאה בטעינת רשימה");
      }
    } catch (e) {
      message.error(e?.message || "שגיאת רשת");
    } finally {
      setLoading(false);
    }
  }, [message, salesAllowed]);

  const loadDetail = useCallback(
    async (chatSessionId) => {
      if (!salesAllowed) return;
      if (!chatSessionId) return;
      setDetailLoading(true);
      try {
        const res = await getCustomerChatSessionDetail(chatSessionId, 400);
        if (res.status === "ok") {
          setDetail(res.payload);
        } else {
          message.error(res.message || "שגיאה בטעינת פרטים");
        }
      } catch (e) {
        message.error(e?.message || "שגיאת רשת");
      } finally {
        setDetailLoading(false);
      }
    },
    [message, salesAllowed]
  );

  useEffect(() => {
    void refreshEntitlements();
  }, [refreshEntitlements]);

  useEffect(() => {
    if (!salesAllowed) {
      setSessions([]);
      return;
    }
    loadList();
  }, [salesAllowed, loadList]);

  const openDetail = (row) => {
    const id = row?.id;
    if (!id) return;
    setSelectedId(id);
    setDetailOpen(true);
    loadDetail(id);
  };

  const onMarkRead = async (unread) => {
    if (!selectedId) return;
    try {
      await markCustomerChatSessionRead(selectedId, unread);
      message.success(unread ? "סומן כלא נקרא" : "סומן כנקרא");
      await loadList();
      await loadDetail(selectedId);
    } catch (e) {
      message.error(e?.message || "שגיאה");
    }
  };

  const onAssign = async () => {
    if (!selectedId || !assignAgentId.trim()) return;
    setAssignBusy(true);
    try {
      await assignCustomerChatSessionLead(selectedId, assignAgentId.trim());
      message.success("הליד שויך");
      setAssignOpen(false);
      setAssignAgentId("");
      await loadList();
      await loadDetail(selectedId);
    } catch (e) {
      message.error(e?.message || "שגיאה בשיוך");
    } finally {
      setAssignBusy(false);
    }
  };

  const openLeadDrawer = async () => {
    const leadId = detail?.lead?.id;
    if (!leadId) return;
    setLeadDrawerOpen(true);
    setLeadDetailLoading(true);
    try {
      const res = await getAiSalesLeadDetail(leadId);
      if (res.status === "ok") {
        setLeadDetail(res.payload);
      } else {
        message.error(res.message || "שגיאה בטעינת ליד");
      }
    } catch (e) {
      message.error(e?.message || "שגיאת רשת");
    } finally {
      setLeadDetailLoading(false);
    }
  };

  const refreshLeadDetail = async () => {
    const leadId = detail?.lead?.id;
    if (!leadId) return;
    try {
      const res = await getAiSalesLeadDetail(leadId);
      if (res.status === "ok") setLeadDetail(res.payload);
    } catch {
      /* drawer shows error */
    }
  };

  const columns = [
    {
      title: "לקוח",
      dataIndex: "customer_identifier",
      key: "customer_identifier",
      ellipsis: true,
      width: 140,
    },
    {
      title: "ליד",
      dataIndex: "lead_id",
      key: "lead_id",
      width: 200,
      ellipsis: true,
      render: (v) =>
        v ? (
          <Typography.Text ellipsis={{ tooltip: String(v) }} style={{ maxWidth: 180 }}>
            {String(v)}
          </Typography.Text>
        ) : (
          "—"
        ),
    },
    {
      title: "סטטוס ליד",
      dataIndex: "lead_status",
      key: "lead_status",
      width: 100,
      render: (v) => v || "—",
    },
    {
      title: "סטטוס session",
      dataIndex: "status",
      key: "status",
      width: 90,
    },
    {
      title: "מצב צ'אט",
      dataIndex: "session_status",
      key: "session_status",
      width: 100,
    },
    {
      title: "נקרא?",
      key: "unread",
      width: 80,
      render: (_, row) =>
        row.unread_for_agent ? <Tag color="red">חדש</Tag> : <Tag>נקרא</Tag>,
    },
    {
      title: "פעילות אחרונה",
      dataIndex: "last_activity_at",
      key: "last_activity_at",
      width: 150,
      render: (v) => fmtTs(v),
    },
    {
      title: "שפה",
      dataIndex: "locale",
      key: "locale",
      width: 70,
    },
    {
      title: "תצוגה מקדימה",
      dataIndex: "last_message_preview",
      key: "last_message_preview",
      ellipsis: true,
      width: 220,
    },
  ];

  const session = detail?.session;
  const messages = detail?.messages || [];
  const summary = detail?.search_summary || {};
  const stateJson = detail?.state_json || {};

  const onUpgradePackage = useCallback(() => {
    if (isAdminUser()) {
      navigate("/p/dashboard/admin/package-features");
      return;
    }
    navigateAgentPackageUpgrade(navigate, "customer_chat_inbox_upgrade");
  }, [navigate]);

  if (entitlementsLoading || entitlementsStatus === "idle") {
    if (!canUseAiSales) {
      return (
        <div className="customer-chat-inbox" dir="rtl" style={{ padding: 48, textAlign: "center" }}>
          <Spin size="large" />
        </div>
      );
    }
  }

  if (!salesAllowed) {
    return (
      <div className="customer-chat-inbox" dir="rtl" style={{ padding: 16 }}>
        <Card>
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              הפיצ׳ר AI Sales זמין בחבילת PRO
            </Typography.Title>
            <Typography.Paragraph style={{ marginBottom: 0 }}>
              קבלו פניות אוטומטיות, ניהול צ׳אט עם לקוחות והגדלת מכירות באמצעות AI
            </Typography.Paragraph>
            <Button type="primary" onClick={onUpgradePackage}>
              שדרוג חבילה
            </Button>
          </Space>
        </Card>
      </div>
    );
  }

  return (
    <div className="customer-chat-inbox" dir="rtl">
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <div className="customer-chat-inbox__header">
          <Typography.Title level={3} style={{ margin: 0 }}>
            צ'אט לקוחות — Inbox
          </Typography.Title>
          <Space wrap>
            <Button type="primary" onClick={copyPublicChatLink}>
              העתק קישור צ'אט ללקוח
            </Button>
            <Button onClick={loadList} loading={loading}>
              רענן
            </Button>
          </Space>
        </div>
        <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
          שיחות ממוצר ה־customer chat (מקור ליד: customer_chat). פתחו שורה לראות היסטוריה,
          state ופרטי חיפוש. להפקת הצעות השתמשו ב&quot;ליד AI Sales&quot;.
        </Typography.Paragraph>

        <div className="customer-chat-inbox__table-wrap">
          <Table
            rowKey="id"
            loading={loading}
            dataSource={sessions}
            columns={columns}
            pagination={{ pageSize: 12, showSizeChanger: false }}
            size="small"
            tableLayout="fixed"
            scroll={{ x: 1180 }}
            onRow={(record) => ({
              onClick: () => openDetail(record),
              style: { cursor: "pointer" },
            })}
          />
        </div>
      </Space>

      <Drawer
        title="פרטי שיחה"
        placement="left"
        width={520}
        onClose={() => {
          setDetailOpen(false);
          setDetail(null);
          setSelectedId(null);
        }}
        open={detailOpen}
        destroyOnHidden
        extra={
          <Space>
            <Button size="small" onClick={() => onMarkRead(false)} disabled={!selectedId}>
              סמן כנקרא
            </Button>
            <Button size="small" onClick={() => onMarkRead(true)} disabled={!selectedId}>
              סמן כלא נקרא
            </Button>
            {isAdmin ? (
              <Button size="small" onClick={() => setAssignOpen(true)} disabled={!selectedId}>
                שייך לסוכן
              </Button>
            ) : null}
            <Button type="primary" size="small" onClick={openLeadDrawer} disabled={!detail?.lead?.id}>
              פתח ליד (AI Sales)
            </Button>
          </Space>
        }
      >
        {detailLoading ? (
          <Typography.Text type="secondary">טוען…</Typography.Text>
        ) : (
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="session id">{session?.id || "—"}</Descriptions.Item>
              <Descriptions.Item label="lead_id">{session?.lead_id || "—"}</Descriptions.Item>
              <Descriptions.Item label="customer_identifier">
                {session?.customer_identifier || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="status">{session?.status || "—"}</Descriptions.Item>
              <Descriptions.Item label="session_status">
                {session?.session_status || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="unread_for_agent">
                {session?.unread_for_agent ? "כן" : "לא"}
              </Descriptions.Item>
              <Descriptions.Item label="last_activity_at">
                {fmtTs(session?.last_activity_at)}
              </Descriptions.Item>
              <Descriptions.Item label="locale">{session?.locale || "—"}</Descriptions.Item>
            </Descriptions>

            <div>
              <Typography.Title level={5}>פרטי חיפוש שנאספו</Typography.Title>
              <Descriptions bordered size="small" column={1}>
                <Descriptions.Item label="יעד">{summary.destination ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="יציאה">{summary.departure_date ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="חזרה">{summary.return_date ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="מבוגרים">{summary.adults ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="ילדים">{summary.children ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="תינוקות">{summary.infants ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="תקציב ₪">{summary.budget_ils ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="לינה / בסיס">
                  {summary.board_preference ?? "—"}
                </Descriptions.Item>
                <Descriptions.Item label="שם לקוח">{summary.customer_name ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="טלפון">{summary.customer_phone ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="אימייל">{summary.customer_email ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="סטטוס ליד">{summary.lead_status ?? "—"}</Descriptions.Item>
              </Descriptions>
            </div>

            <div>
              <Typography.Title level={5}>state_json</Typography.Title>
              <pre className="customer-chat-inbox__json">
                {JSON.stringify(stateJson, null, 2)}
              </pre>
            </div>

            <div>
              <Typography.Title level={5}>הודעות</Typography.Title>
              <List
                size="small"
                bordered
                dataSource={messages}
                locale={{ emptyText: "אין הודעות" }}
                renderItem={(m) => (
                  <List.Item>
                    <List.Item.Meta
                      title={
                        <Space>
                          <Tag>{m.role}</Tag>
                          <Typography.Text type="secondary">{fmtTs(m.created_at)}</Typography.Text>
                          {m.detected_intent ? (
                            <Tag color="blue">{m.detected_intent}</Tag>
                          ) : null}
                        </Space>
                      }
                      description={
                        String(m.role || "").toLowerCase() === "assistant" ? (
                          <CustomerVacationChatAssistantText text={m.message_text} />
                        ) : (
                          m.message_text
                        )
                      }
                    />
                  </List.Item>
                )}
              />
            </div>

            <Typography.Paragraph type="secondary">
              להפקת הצעות: פתחו את הליד ב-AI Sales והריצו &quot;הפק הצעות&quot; משם (אותו lead_id).
            </Typography.Paragraph>
          </Space>
        )}
      </Drawer>

      <Modal
        title="שיוך ליד לסוכן"
        open={assignOpen}
        onCancel={() => setAssignOpen(false)}
        onOk={onAssign}
        confirmLoading={assignBusy}
        okText="שייך"
        cancelText="ביטול"
      >
        <Typography.Paragraph type="secondary">
          מזהה סוכן (מספר) — פעולה לאדמין בלבד.
        </Typography.Paragraph>
        <Input
          placeholder="למשל 16"
          value={assignAgentId}
          onChange={(e) => setAssignAgentId(e.target.value)}
        />
      </Modal>

      <LeadDetailsDrawer
        open={leadDrawerOpen}
        onClose={() => {
          setLeadDrawerOpen(false);
          setLeadDetail(null);
        }}
        detail={leadDetail}
        loading={leadDetailLoading}
        onRefresh={refreshLeadDetail}
      />
    </div>
  );
}
